import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, CheckCircle2, Loader2, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * VerifyEmail
 *
 * Lands here from the post-signup redirect when the user has completed their
 * first trial call and now needs to verify before starting more. Also linked
 * from a "Verify email" banner inside the app.
 */
export default function VerifyEmail() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // If the user is already verified, bounce to dashboard immediately
  useEffect(() => {
    if (user?.email_verified) navigate('/', { replace: true });
  }, [user, navigate]);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (code.replace(/\D/g, '').length !== 6) {
      toast.error('Please enter the 6-digit code from your email.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-email`, { code: code.trim() });
      toast.success("You're verified! Welcome back.");
      if (refreshUser) await refreshUser();
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`);
      toast.success('A fresh code has been sent. Check your inbox.');
      setResendCooldown(60);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      const match = detail.match(/(\d+)s/);
      if (match) setResendCooldown(parseInt(match[1], 10));
      toast.error(detail || 'Could not resend code. Try again shortly.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
      <div className="w-full max-w-md">
        <Card className="border-zinc-200" data-testid="verify-email-card">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-full bg-brand-accent-muted flex items-center justify-center">
                <Mail className="w-4 h-4 text-brand-primary" />
              </div>
              <CardTitle className="text-xl">Verify your email</CardTitle>
            </div>
            <p className="text-sm text-zinc-600 leading-relaxed">
              We sent a 6-digit code to <strong>{user?.email}</strong>. Enter it below to keep using PipeGuide after your first trial call.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="123456"
                  className="text-center text-2xl tracking-[0.6em] font-mono h-14"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  data-testid="verify-code-input"
                  required
                />
                <p className="text-xs text-zinc-500 text-center">Code expires in 15 minutes.</p>
              </div>
              <Button
                type="submit"
                className="w-full bg-brand-primary hover:bg-brand-hover"
                disabled={loading || code.length !== 6}
                data-testid="verify-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {loading ? 'Verifying…' : 'Verify Email'}
              </Button>
              <div className="text-center text-sm">
                <button
                  type="button"
                  onClick={resend}
                  disabled={resending || resendCooldown > 0}
                  className="text-brand-primary hover:underline disabled:text-zinc-400 disabled:cursor-not-allowed"
                  data-testid="resend-code-btn"
                >
                  {resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : (resending ? 'Sending…' : "Didn't get it? Resend code")}
                </button>
              </div>
            </form>

            <div className="mt-6 pt-4 border-t border-zinc-200">
              <div className="flex items-start gap-2.5 bg-brand-accent-muted rounded-md px-3 py-2.5">
                <Sparkles className="w-4 h-4 text-brand-primary mt-0.5 flex-shrink-0" />
                <div className="text-xs text-zinc-700 leading-relaxed">
                  Verifying your email proves you&apos;re a real user, unlocks unlimited trial calls, and lets you invite teammates to earn +30 free minutes.
                </div>
              </div>
            </div>

            <div className="mt-4 text-center text-xs text-zinc-500">
              Wrong email? <Link to="/login" className="text-brand-primary hover:underline" data-testid="back-to-login-link">Sign in again</Link>
              <span className="mx-2">·</span>
              <Link to="/" className="text-brand-primary hover:underline" data-testid="back-to-dashboard-link">
                Back to dashboard <ArrowRight className="w-3 h-3 inline" />
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
